import { useIsMutating, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import useSystemSetting from '@/hooks/files/use-system-setting';
import { ResponseError, responseScheme } from '@/models/response';
import type { Uploadable } from '@/models/uploadable';
import { promiseLogger, SENTRY_LOG } from '@/services/sentry';
import { addToast, addTranslateToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { readableFileSize } from '@/utils/file-size';
import { isRetryEnabledError, RETRY_DELAYS } from '@/utils/query-client';
import { convertUploadableToFile } from '@/utils/uploadable';

export default function useUploadFile() {
  const { t } = useTranslation();
  const { getFileSizeLimit, isLoading: isSystemSettingLoading } = useSystemSetting();

  const mutation = useMutation<UploadFileResponseType, ResponseError, Uploadable>({
    mutationKey: ['/UploadFile'],
    mutationFn: async (params) =>
      fetcher(
        '/UploadFile',
        { file: await convertUploadableToFile(params) },
        {
          schema: uploadFileResponseScheme,
          requestContentType: 'form-data',
          validResponseCodes: [0],
          timeout: 300_000,
          authPlacement: 'Data',
          target: 'file-upload',
        },
      ),
    retry: (failureCount, error) => isRetryEnabledError(error) && failureCount <= 5,
    retryDelay: (failureCount) => RETRY_DELAYS[failureCount] ?? 10000,
    onError: (error) => {
      if (ResponseError.isResponseError(error)) {
        addToast({
          type: 'error',
          message: error.message,
        });
      } else {
        addTranslateToast({
          type: 'error',
          message: 'upload.failed',
        });
      }
    },
  });

  const upload = async (input: Uploadable, type?: Nullable<string>): Promise<string> => {
    mutation.reset();

    const fileSizeLimit = getFileSizeLimit(type);
    if (input.size > fileSizeLimit * 1.05) {
      addToast({
        type: 'error',
        message: t('errors.file-size-limit', { limit: readableFileSize(fileSizeLimit) }),
      });
      throw 'FILE_TOO_LARGE';
    }

    return await promiseLogger({
      name: SENTRY_LOG.UPLOAD,
      requestAttributes: {
        'file-size': `${input.size}`,
        'file-type': input.type,
      },
      action: () => mutation.mutateAsync(input).then((res) => res.FileUrl),
    });
  };

  return {
    upload,
    isLoading: isSystemSettingLoading || mutation.isPending,
    ...mutation,
  };
}

export function useIsUploadingFile() {
  const uploadingCount = useIsMutating({ mutationKey: ['/UploadFile'] });

  return uploadingCount > 0;
}

export const uploadFileResponseScheme = responseScheme.extend({
  FileUrl: z.string().url(),
});

export type UploadFileResponseType = z.infer<typeof uploadFileResponseScheme>;
